import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import { useTranslation } from "react-i18next";
import type {
  Artifact,
  ArtifactList,
  ArtifactTable,
  ArtifactText,
  Citation,
} from "@/api/types";
import type { HighlightEntity } from "@/components/artifacts/entityHighlight";
import { HighlightedText } from "@/components/artifacts/HighlightedText";
import { InlineCitedText } from "@/components/artifacts/InlineCitedText";
import { hasInlineMarkers } from "@/components/artifacts/inlineCitations";

export interface ArtifactRendererProps<T extends Artifact = Artifact> {
  artifact: T;
  /**
   * Optional click-through for inline citation chips. Threaded down from
   * `ArtifactView` → meeting-card. Omitted for renderers that don't need
   * citation support (e.g. unknown payloads).
   */
  onJump?: (startMs: number) => void;
  /**
   * Phase 6 — bound employee/client entities highlighted inside the
   * artifact text. When empty/undefined the renderer is a no-op visually.
   */
  highlightEntities?: readonly HighlightEntity[];
}

/**
 * Text artifact: title + markdown body. `rehype-sanitize` strips <script>,
 * inline `on*` handlers, and other XSS vectors from LLM-generated content.
 * UI_PLAN §9.2.
 *
 * When the backend embeds `[[cN]]` markers in `body`, we bypass react-markdown
 * for the citation-aware inline renderer so the chips stay anchored next to
 * the facts they support. Markers + markdown are mutually exclusive in v1.1.4
 * (the LLM is prompted to emit one or the other, not both) — when markers are
 * present we treat the body as plain text with line breaks.
 */
export function TextArtifactRenderer({
  artifact,
  onJump,
  highlightEntities,
}: ArtifactRendererProps<ArtifactText>) {
  const citations = artifact.citations ?? [];
  const inline = hasInlineMarkers(artifact.body);
  const entities = highlightEntities ?? [];

  return (
    <section data-testid="artifact-text">
      <h3 className="text-base font-semibold">{artifact.title}</h3>
      <div
        className="prose prose-sm mt-2 max-w-none text-sm"
        data-testid="artifact-text-body"
      >
        {inline ? (
          <InlineParagraphs
            text={artifact.body}
            citations={citations}
            onJump={onJump}
            highlightEntities={entities}
          />
        ) : entities.length > 0 ? (
          // Highlighted plain-text mode bypasses ReactMarkdown — the markdown
          // AST would split highlight matches across nodes (`<em>Иван</em> Петров`)
          // and break the entity contract. Markdown features inside a body
          // that also has matching bindings degrade to plain text + linebreaks.
          <p className="whitespace-pre-line">
            <HighlightedText text={artifact.body} entities={entities} />
          </p>
        ) : (
          <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
            {artifact.body}
          </ReactMarkdown>
        )}
      </div>
    </section>
  );
}

export function ListArtifactRenderer({
  artifact,
  onJump,
  highlightEntities,
}: ArtifactRendererProps<ArtifactList>) {
  const citations = artifact.citations ?? [];
  const entities = highlightEntities ?? [];
  return (
    <section data-testid="artifact-list">
      <h3 className="text-base font-semibold">{artifact.title}</h3>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
        {artifact.items.map((item, i) => (
          <li key={i}>
            {hasInlineMarkers(item) ? (
              <InlineCitedText
                text={item}
                citations={citations}
                onJump={onJump}
              />
            ) : entities.length > 0 ? (
              <HighlightedText text={item} entities={entities} />
            ) : (
              item
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function TableArtifactRenderer({
  artifact,
  onJump,
  highlightEntities,
}: ArtifactRendererProps<ArtifactTable>) {
  // v1.1.5: cells with `[[cN]]` markers render through InlineCitedText so the
  // chip sits next to the supported fact instead of falling out into a giant
  // bottom block (operator feedback v1.1.4 → v1.1.5). Cells without markers
  // stay as plain text to keep table layout stable. Headers are never
  // marker-bearing — the LLM is prompted to put markers in cell values only.
  const citations = artifact.citations ?? [];
  const entities = highlightEntities ?? [];
  return (
    <section data-testid="artifact-table">
      <h3 className="text-base font-semibold">{artifact.title}</h3>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              {artifact.columns.map((col, i) => (
                <th
                  key={i}
                  className="border-b border-input px-2 py-1 text-left font-medium"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {artifact.rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci} className="border-b border-input/50 px-2 py-1">
                    {hasInlineMarkers(cell) ? (
                      <InlineCitedText
                        text={cell}
                        citations={citations}
                        onJump={onJump}
                        highlightEntities={entities}
                      />
                    ) : entities.length > 0 ? (
                      <HighlightedText text={cell} entities={entities} />
                    ) : (
                      cell
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function UnknownArtifactRenderer({ artifact }: { artifact: unknown }) {
  const { t } = useTranslation();
  const title =
    typeof artifact === "object" && artifact !== null && "title" in artifact
      ? String((artifact as { title?: unknown }).title ?? "")
      : "";
  return (
    <section data-testid="artifact-unknown">
      <h3 className="text-base font-semibold">{title}</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        {t("artifact.unknown")}
      </p>
      <details className="mt-2">
        <summary className="cursor-pointer text-xs text-muted-foreground">
          {t("artifact.raw")}
        </summary>
        <pre className="mt-2 overflow-x-auto rounded bg-muted p-2 text-xs">
          {JSON.stringify(artifact, null, 2)}
        </pre>
      </details>
    </section>
  );
}

interface InlineParagraphsProps {
  text: string;
  citations: Citation[];
  onJump?: (startMs: number) => void;
  highlightEntities?: readonly HighlightEntity[];
}

/**
 * Split a marker-bearing body into paragraphs on blank lines, then render each
 * paragraph through InlineCitedText. We can't run the body through
 * react-markdown without losing the marker-positioning guarantee (the
 * markdown AST normalises whitespace and would split the chip from its
 * supporting clause across nodes).
 */
function InlineParagraphs({
  text,
  citations,
  onJump,
  highlightEntities,
}: InlineParagraphsProps) {
  // Normalise CRLF → LF, then split on blank lines. Single line breaks are
  // preserved inside paragraphs as `\n` characters which the `whitespace-pre-line`
  // class renders visually.
  const paragraphs = text.replace(/\r\n/g, "\n").split(/\n{2,}/);
  return (
    <>
      {paragraphs.map((p, i) => (
        <p key={i} className="whitespace-pre-line">
          <InlineCitedText
            text={p}
            citations={citations}
            onJump={onJump}
            highlightEntities={highlightEntities}
          />
        </p>
      ))}
    </>
  );
}
